/**
 * Paleta da clínica a partir de UMA cor hex (docs/04 §3.3):
 *  1. converte para OKLCH (mantém o matiz da marca ao variar a luminosidade);
 *  2. garante texto branco sobre a primária com contraste ≥ 4,5:1 (WCAG AA) — a mesma cor
 *     usada como link sobre branco também passa; se não passar, escurece até passar e avisa;
 *  3. gera hover, fundo suave e a variante para modo escuro.
 * Cores de estado (erro/alerta/sucesso) NÃO passam por aqui: são fixas da plataforma.
 */
export interface Oklch { l: number; c: number; h: number }

const srgbToLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

export function hexToOklch(hex: string): Oklch {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) throw new Error(`cor inválida: ${hex}`);
  const n = parseInt(m[1]!, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => srgbToLinear(v / 255)) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const mm = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * mm - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * mm + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * mm - 0.808675766 * s;
  const h = (Math.atan2(B, A) * 180) / Math.PI;
  return { l: L, c: Math.hypot(A, B), h: h < 0 ? h + 360 : h };
}

/** Luminância relativa WCAG de uma cor OKLCH (convertida para sRGB linear, com clamp de gamut). */
export function luminance({ l, c, h }: Oklch): number {
  const a = c * Math.cos((h * Math.PI) / 180);
  const b = c * Math.sin((h * Math.PI) / 180);
  const l_ = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m_ = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s_ = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const R = clamp01(4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_);
  const G = clamp01(-1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_);
  const Bl = clamp01(-0.0041960771 * l_ - 0.7034186147 * m_ + 1.707614701 * s_);
  return 0.2126 * R + 0.7152 * G + 0.0722 * Bl;
}

export const contrast = (y1: number, y2: number) => (Math.max(y1, y2) + 0.05) / (Math.min(y1, y2) + 0.05);
const WHITE_Y = 1;

const fmt = ({ l, c, h }: Oklch) => `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${h.toFixed(1)})`;

export interface BrandTheme {
  css: string;
  /** Mesmas variáveis em objeto, para aplicar num container (pré-visualização) via style. */
  vars: Record<string, string>;
  darkVars: Record<string, string>;
  /** true se a cor precisou ser escurecida para garantir leitura. */
  adjusted: boolean;
  primary: Oklch;
  contrastOnWhite: number;
}

export function brandTheme(primaryHex: string, accentHex?: string | null): BrandTheme {
  const base = hexToOklch(primaryHex);
  let primary = { ...base };
  // Escurece até o branco sobre a cor (e a cor sobre o branco) atingir 4,5:1.
  while (contrast(WHITE_Y, luminance(primary)) < 4.5 && primary.l > 0.2) {
    primary = { ...primary, l: primary.l - 0.01 };
  }
  const adjusted = primary.l < base.l - 0.005;
  const chroma = Math.min(primary.c, 0.2);
  const p = { ...primary, c: chroma };
  const hover = { ...p, l: Math.max(0.15, p.l - 0.05) };
  const soft = { l: 0.955, c: Math.min(chroma * 0.3, 0.035), h: p.h };
  const darkP = { l: Math.min(0.8, Math.max(0.68, base.l + 0.25)), c: Math.min(chroma, 0.12), h: p.h };
  const darkSoft = { l: 0.3, c: Math.min(chroma * 0.4, 0.05), h: p.h };
  const accent = accentHex ? hexToOklch(accentHex) : null;

  const vars: Record<string, string> = {
    '--dnx-primary': fmt(p), '--dnx-primary-hover': fmt(hover), '--dnx-primary-soft': fmt(soft),
    '--dnx-primary-fg': 'oklch(0.99 0 0)',
    ...(accent ? { '--dnx-accent': fmt(accent) } : {}),
  };
  const darkVars: Record<string, string> = {
    '--dnx-primary': fmt(darkP), '--dnx-primary-hover': fmt({ ...darkP, l: darkP.l + 0.05 }),
    '--dnx-primary-soft': fmt(darkSoft), '--dnx-primary-fg': `oklch(0.18 0.02 ${p.h.toFixed(1)})`,
  };
  const decl = (v: Record<string, string>) => Object.entries(v).map(([k, val]) => `${k}:${val}`).join(';');

  return {
    css: `:root{${decl(vars)}}@media (prefers-color-scheme: dark){:root:not([data-theme='light']){${decl(darkVars)}}}`,
    vars,
    darkVars,
    adjusted,
    primary: p,
    contrastOnWhite: contrast(WHITE_Y, luminance(p)),
  };
}

/** Injeta (ou atualiza) o tema da clínica no <head>, antes da primeira renderização. */
export function applyBrandTheme(primaryHex: string, accentHex?: string | null): BrandTheme {
  const theme = brandTheme(primaryHex, accentHex);
  let el = document.getElementById('dnx-brand-theme');
  if (!el) {
    el = document.createElement('style');
    el.id = 'dnx-brand-theme';
    document.head.appendChild(el);
  }
  el.textContent = theme.css;
  return theme;
}
